import {
  useState, useEffect, useMemo, useCallback,
} from 'react';

import debounce from 'lodash/debounce';

import breakpoints from '@edx/paragon/dist/utils/breakpoints';

type TypeBreakpoint = 'extraSmall' | 'small' | 'medium' | 'large' | 'extraLarge' | number

/**
 * A React hook used to determine if the current viewport width matches the max-width breakpoint provided.
 * These values may get outdated with Paragon updates:
 *
 * `extraSmall` - `576`
 *
 * `small` - `768`
 *
 * `medium` - `992`
 *
 * `large` - `1200`
 *
 * `extraLarge` - `1400`
 *
 * @param breakpoint - Paragon breakpoint string name or number.
 * Number should be used in edge cases only.
 * Defaults to `small`.
 *
 * Returns `true` if the viewport width is smaller than the provided breakpoint.
 */
const useBreakpointMatch = (breakpoint: TypeBreakpoint) => {
  const [matchesBreakpoint, setMatchesBreakpoint] = useState(false);

  const value = useMemo(() => (typeof breakpoint === 'number' ? breakpoint : breakpoints[breakpoint]?.maxWidth || breakpoints.small.maxWidth), [breakpoint]);

  const checkForBreakpoint = useCallback(() => {
    setMatchesBreakpoint(window.matchMedia(`(max-width: ${value}px)`).matches);
  }, [value]);

  const debouncedCheckForBreakpoint = useMemo(() => debounce(checkForBreakpoint, 100), [checkForBreakpoint]);

  useEffect(() => {
    checkForBreakpoint();
    window.addEventListener('resize', debouncedCheckForBreakpoint);

    return () => window.removeEventListener('resize', debouncedCheckForBreakpoint);
  }, [checkForBreakpoint]);

  return matchesBreakpoint;
};

export default useBreakpointMatch;
